// When a product or a category may be sold (spec/13 §6.1, F-FNB-02): a short list of windows — the days and a
// from–to time, in the **venue's** timezone ("breakfast only", "after 18:00"). No window is "any time", which is
// every item's default. A window whose end is before its start runs past midnight into the next day. Outside
// every window the item stays on the menu, disabled, and the server refuses a cart naming it.

import type { TimeWindow } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Chip, TextField } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';

type Day = Exclude<TimeWindow['days'][number], 'holiday'>;
const DAYS: readonly Day[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** A new window: every day, from 18:00 to the end of the day — the shape "after 18:00" takes. */
const NEW_WINDOW: TimeWindow = { days: [...DAYS], start: '18:00', end: '23:59' };

/** Whether every window names a day and two times (`HH:MM`) — what the contract accepts. */
export function schedulesValid(windows: readonly TimeWindow[]): boolean {
  const time = /^([01]\d|2[0-3]):[0-5]\d$/;
  return windows.every((window) => window.days.length > 0 && time.test(window.start) && time.test(window.end) && window.start !== window.end);
}

/** The schedule in a few words, for a row: "Any time", or "18:00–23:59" (+ how many more). */
export function scheduleSummary(windows: readonly TimeWindow[], t: (key: string, options?: Record<string, unknown>) => string): string {
  const first = windows[0];
  if (first === undefined) return t('menu.schedule.anyTime');
  const span = t('menu.schedule.span', { from: first.start, to: first.end });
  return windows.length > 1 ? t('menu.schedule.more', { span, count: windows.length - 1 }) : span;
}

export function ScheduleField({ windows, onChange }: { readonly windows: readonly TimeWindow[]; readonly onChange: (windows: TimeWindow[]) => void }): ReactNode {
  const { t } = useTranslation('console');
  const update = (index: number, next: TimeWindow) => onChange(windows.map((window, at) => (at === index ? next : window)));

  return (
    <fieldset className="console-menu-schedule">
      <legend className="clav-field__label">{t('menu.schedule.label')}</legend>
      <p className="console-muted">{windows.length === 0 ? t('menu.schedule.anyTimeHint') : t('menu.schedule.hint')}</p>
      {windows.map((window, index) => (
        <div key={index} className="console-menu-schedule__window" role="group" aria-label={t('menu.schedule.window', { number: index + 1 })}>
          <div className="console-menu-schedule__days">
            {DAYS.map((day) => (
              <Chip
                key={day}
                selected={window.days.includes(day)}
                onSelectedChange={(selected) =>
                  update(index, { ...window, days: selected ? DAYS.filter((one) => one === day || window.days.includes(one)) : window.days.filter((one) => one !== day) })
                }
              >
                {t(`menu.schedule.days.${day}`)}
              </Chip>
            ))}
          </div>
          <div className="console-menu-schedule__times">
            <TextField type="time" label={t('menu.schedule.from')} value={window.start} onChange={(event) => update(index, { ...window, start: event.target.value })} />
            <TextField type="time" label={t('menu.schedule.to')} value={window.end} onChange={(event) => update(index, { ...window, end: event.target.value })} />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              icon={<Icon name="close" />}
              label={t('menu.schedule.remove', { number: index + 1 })}
              onClick={() => onChange(windows.filter((_, at) => at !== index))}
            />
          </div>
        </div>
      ))}
      <div>
        <Button type="button" size="sm" variant="secondary" icon={<Icon name="plus" />} onClick={() => onChange([...windows, NEW_WINDOW])}>
          {t('menu.schedule.add')}
        </Button>
      </div>
    </fieldset>
  );
}
